/* eslint-disable react/prop-types */
import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Card,
  Chip,
  Alert,
  CircularProgress,
  alpha,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  CreditCard as StripeIcon,
  CurrencyBitcoin as BinanceIcon,
  AccountBalanceWallet as ZelleIcon,
  PhoneIphone as PagoMovilIcon,
  AccountBalance as BankIcon,
  CloudUpload as UploadIcon,
  Description as FileIcon,
  CheckCircle as CheckIcon,
  LocalOffer as PromoIcon,
  Cancel as RemoveIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getApi, postApi } from '../views/services/api';
import { constant } from '../views/constant';

// ---------------------------------------------------------------------------
// Metadatos visuales por método. Mantenidos en sync con el backend (code).
// ---------------------------------------------------------------------------
const METHOD_META = {
  stripe: { label: 'Stripe (Tarjeta)', icon: StripeIcon, color: '#635BFF' },
  binance: { label: 'Binance Pay', icon: BinanceIcon, color: '#F0B90B' },
  zelle: { label: 'Zelle', icon: ZelleIcon, color: '#6D1ED4' },
  pago_movil: { label: 'Pago Móvil', icon: PagoMovilIcon, color: '#26A69A' },
  bank_transfer: { label: 'Transferencia Bancaria', icon: BankIcon, color: '#1E88E5' }
};

const STEPS = ['Resumen', 'Método de pago', 'Confirmación'];

export default function PaymentModel(props) {
  const { openPayment, handleClosePayment, purchaseData, addPayments } = props;
  const theme = useTheme();

  const [activeStep, setActiveStep] = useState(0);
  const [gateways, setGateways] = useState([]);
  const [loadingGateways, setLoadingGateways] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ----- Código promocional -----
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // { code, discountAmount, finalAmount, originalAmount, ... }
  const [validatingPromo, setValidatingPromo] = useState(false);

  // Monto efectivo (con descuento si aplica)
  const effectiveAmount = promoApplied ? promoApplied.finalAmount : Number(purchaseData?.amount) || 0;

  const selectedGateway = useMemo(
    () => gateways.find((g) => g.code === selectedCode),
    [gateways, selectedCode]
  );

  // Reset cuando se cierra/abre el modal
  useEffect(() => {
    if (openPayment) {
      setActiveStep(0);
      setSelectedCode('');
      setConfirmationNumber('');
      setProofFile(null);
      setPromoInput('');
      setPromoApplied(null);
      loadGateways();
    }
    // eslint-disable-next-line
  }, [openPayment]);

  const loadGateways = async () => {
    setLoadingGateways(true);
    try {
      const res = await getApi('api/payment-gateways/active');
      if (res && res.status === 200) {
        setGateways(res.data?.gateways || []);
      }
    } catch (e) {
      console.error('Error loading gateways', e);
    } finally {
      setLoadingGateways(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    handleClosePayment();
  };

  const handleNext = () => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const isManualMethod = (code) => ['binance', 'zelle', 'pago_movil', 'bank_transfer'].includes(code);

  // -------------------------------------------------------------------------
  // Promo code: validar contra el backend
  // -------------------------------------------------------------------------
  const handleApplyPromo = async () => {
    if (!promoInput.trim()) {
      toast.error('Ingresa un código');
      return;
    }
    setValidatingPromo(true);
    try {
      const hotelId = JSON.parse(localStorage.getItem('hotelData'))._id;
      const res = await postApi('api/platform-promo-codes/validate', {
        code: promoInput.trim(),
        hotelId,
        subscriptionId: purchaseData._id,
        amount: Number(purchaseData.amount) || 0
      });
      if (res?.status === 200 && res.data?.valid) {
        setPromoApplied(res.data);
        toast.success(res.data.message || 'Código aplicado');
      } else {
        setPromoApplied(null);
        toast.error(res?.data?.message || 'Código inválido');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error validando el código');
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoInput('');
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!selectedGateway) {
      toast.error('Selecciona un método de pago');
      return;
    }
    setSubmitting(true);

    try {
      // ----- Stripe: redirigir al Checkout -----
      if (selectedCode === 'stripe') {
        const sessionRes = await postApi('api/payments/stripe/create-session', {
          subscription_id: purchaseData._id,
          title: purchaseData.title,
          days: purchaseData.days,
          amount: purchaseData.amount,
          description: purchaseData.description,
          createdBy: JSON.parse(localStorage.getItem('hotelData'))._id,
          promoCode: promoApplied ? promoApplied.code : '',
          successUrl: `${window.location.origin}/dashboard/hotelpayments/stripe-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/dashboard/hotelsubscriptions`
        });
        if (sessionRes?.status === 200 && sessionRes.data?.url) {
          window.location.href = sessionRes.data.url;
          return;
        }
        toast.error('No se pudo iniciar el pago con Stripe');
        setSubmitting(false);
        return;
      }

      // ----- Manuales: multipart con comprobante -----
      const fd = new FormData();
      fd.append('subscription_id', purchaseData._id);
      fd.append('title', purchaseData.title);
      fd.append('days', purchaseData.days);
      fd.append('amount', purchaseData.amount);
      fd.append('description', purchaseData.description || '');
      fd.append('createdBy', JSON.parse(localStorage.getItem('hotelData'))._id);
      fd.append('paymentMethod', selectedCode);
      fd.append('confirmationNumber', confirmationNumber);
      if (promoApplied?.code) fd.append('promoCode', promoApplied.code);
      if (proofFile) fd.append('proof', proofFile);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.post(constant.baseUrl + 'api/payments/manual', fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res?.status === 201) {
        toast.success('Plan adquirido. Comprobante registrado correctamente.');
        // Liberar el paywall (mismo patrón que ya existía)
        try {
          const hotelDataStr = localStorage.getItem('hotelData');
          if (hotelDataStr) {
            const hotelData = JSON.parse(hotelDataStr);
            hotelData.subscriptionStatus = 'active';
            localStorage.setItem('hotelData', JSON.stringify(hotelData));
          }
        } catch (e) {
          /* noop */
        }
        handleClosePayment();
        // Notificar al padre por compatibilidad con el flujo viejo
        if (typeof addPayments === 'function') {
          // sin re-crear; ya está creado en el backend, sólo refrescamos lista del padre
          // pasando una bandera vacía; el padre llama getAllPaymentsDetails internamente.
          try {
            await addPayments({ ...purchaseData, _alreadyPersisted: true });
          } catch (e) {
            /* noop */
          }
        }
        setTimeout(() => {
          window.location.href = '/dashboard/app';
        }, 1200);
      } else {
        toast.error(res?.data?.error || 'No se pudo registrar el pago');
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Error procesando el pago');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render por paso
  // -------------------------------------------------------------------------
  const renderSummary = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Estás a punto de adquirir el siguiente plan:
      </Typography>
      <Card sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <Stack spacing={1}>
          <Typography variant="h6" fontWeight={700}>
            {purchaseData?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {purchaseData?.description}
          </Typography>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Duración</Typography>
            <Typography variant="body2" fontWeight={600}>
              {purchaseData?.days} días
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Subtotal</Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={
                promoApplied
                  ? { textDecoration: 'line-through', color: 'text.secondary' }
                  : {}
              }
            >
              ${purchaseData?.amount} USD
            </Typography>
          </Stack>
          {promoApplied && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="success.main">
                Descuento ({promoApplied.code})
              </Typography>
              <Typography variant="body2" fontWeight={600} color="success.main">
                -${promoApplied.discountAmount} USD
              </Typography>
            </Stack>
          )}
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>
              Total a pagar
            </Typography>
            <Typography variant="h6" color="success.main" fontWeight={700}>
              ${effectiveAmount} USD
            </Typography>
          </Stack>
        </Stack>
      </Card>

      {/* ------- Bloque Código Promocional ------- */}
      <Card
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderStyle: promoApplied ? 'solid' : 'dashed',
          borderColor: promoApplied
            ? alpha(theme.palette.success.main, 0.5)
            : alpha(theme.palette.warning.main, 0.5),
          bgcolor: promoApplied
            ? alpha(theme.palette.success.main, 0.05)
            : alpha(theme.palette.warning.main, 0.04)
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PromoIcon sx={{ color: promoApplied ? 'success.main' : 'warning.main' }} />
            <Typography variant="subtitle2" fontWeight={700}>
              {promoApplied ? 'Código aplicado' : '¿Tienes un código promocional?'}
            </Typography>
          </Stack>

          {!promoApplied && (
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                placeholder="Ingresa tu código"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 1 } }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleApplyPromo();
                }}
                disabled={validatingPromo}
              />
              <Button
                variant="contained"
                color="warning"
                onClick={handleApplyPromo}
                disabled={!promoInput.trim() || validatingPromo}
                startIcon={
                  validatingPromo ? <CircularProgress size={14} color="inherit" /> : null
                }
              >
                Aplicar
              </Button>
            </Stack>
          )}

          {promoApplied && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack>
                <Chip
                  size="small"
                  color="success"
                  label={promoApplied.code}
                  sx={{ fontWeight: 700, fontFamily: 'monospace', alignSelf: 'flex-start' }}
                />
                <Typography variant="caption" color="success.main" mt={0.5}>
                  {promoApplied.message}
                </Typography>
              </Stack>
              <Button
                size="small"
                color="inherit"
                startIcon={<RemoveIcon />}
                onClick={handleRemovePromo}
              >
                Quitar
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );

  const renderMethodPicker = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Selecciona cómo quieres pagar:
      </Typography>

      {loadingGateways && (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {!loadingGateways && gateways.length === 0 && (
        <Alert severity="warning">
          El administrador del sistema aún no ha habilitado ningún método de pago. Contáctalo para activarlos.
        </Alert>
      )}

      <Stack spacing={1.5}>
        {gateways.map((g) => {
          const meta = METHOD_META[g.code] || {};
          const Icon = meta.icon || StripeIcon;
          const selected = selectedCode === g.code;
          return (
            <Card
              key={g.code}
              onClick={() => setSelectedCode(g.code)}
              sx={{
                p: 2,
                cursor: 'pointer',
                borderRadius: 2,
                border: `2px solid ${selected ? meta.color || theme.palette.primary.main : alpha(theme.palette.divider, 0.5)}`,
                bgcolor: selected ? alpha(meta.color || theme.palette.primary.main, 0.06) : 'transparent',
                transition: 'all 0.2s',
                '&:hover': { borderColor: meta.color || theme.palette.primary.main }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: alpha(meta.color || theme.palette.primary.main, 0.12),
                    color: meta.color || theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Icon />
                </Box>
                <Box flex={1}>
                  <Typography variant="body1" fontWeight={600}>
                    {g.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.type === 'automatic' ? 'Pago automático' : 'Pago manual con comprobante'}
                  </Typography>
                </Box>
                {selected && <CheckIcon sx={{ color: meta.color || theme.palette.primary.main }} />}
              </Stack>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );

  const renderInstructionsBlock = () => {
    if (!selectedGateway) return null;
    const cfg = selectedGateway.config || {};
    const meta = METHOD_META[selectedGateway.code] || {};

    const renderRow = (label, value) =>
      value ? (
        <Stack direction="row" justifyContent="space-between" key={label}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all', textAlign: 'right' }}>
            {value}
          </Typography>
        </Stack>
      ) : null;

    let rows = null;
    if (selectedGateway.code === 'binance') {
      rows = (
        <>
          {renderRow('Red', cfg.network)}
          {renderRow('Wallet', cfg.walletAddress)}
        </>
      );
    } else if (selectedGateway.code === 'zelle') {
      rows = (
        <>
          {renderRow('Beneficiario', cfg.recipientName)}
          {renderRow('Email Zelle', cfg.email)}
        </>
      );
    } else if (selectedGateway.code === 'pago_movil') {
      rows = (
        <>
          {renderRow('Banco', cfg.bank)}
          {renderRow('Teléfono', cfg.phone)}
          {renderRow('Cédula/RIF', cfg.idNumber)}
          {renderRow('Titular', cfg.accountHolder)}
        </>
      );
    } else if (selectedGateway.code === 'bank_transfer') {
      rows = (
        <>
          {renderRow('Banco', cfg.bankName)}
          {renderRow('N° Cuenta', cfg.accountNumber)}
          {renderRow('Tipo de Cuenta', cfg.accountType)}
          {renderRow('Titular', cfg.accountHolder)}
          {renderRow('Routing', cfg.routingNumber)}
          {renderRow('SWIFT', cfg.swiftCode)}
        </>
      );
    }

    return (
      <Card sx={{ p: 2, borderRadius: 2, bgcolor: alpha(meta.color || theme.palette.primary.main, 0.06) }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ color: meta.color }}>
            Datos para el pago
          </Typography>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Monto a transferir
            </Typography>
            <Typography variant="body2" fontWeight={700} color="success.main">
              ${effectiveAmount} USD
              {promoApplied && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 0.5, textDecoration: 'line-through', color: 'text.secondary' }}
                >
                  ${purchaseData?.amount}
                </Typography>
              )}
            </Typography>
          </Stack>
          {rows}
          {selectedGateway.instructions && (
            <>
              <Divider />
              <Typography variant="caption" color="text.secondary">
                {selectedGateway.instructions}
              </Typography>
            </>
          )}
        </Stack>
      </Card>
    );
  };

  const renderConfirmation = () => {
    if (!selectedGateway) {
      return <Alert severity="warning">Vuelve y selecciona un método de pago.</Alert>;
    }

    if (selectedGateway.code === 'stripe') {
      return (
        <Stack spacing={2}>
          <Alert severity="info">
            Serás redirigido al sitio seguro de Stripe para completar el pago. Al volver, tu plan se activará automáticamente.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Pago de <strong>${effectiveAmount} USD</strong> por <strong>{purchaseData?.title}</strong>
            {promoApplied && (
              <>
                {' '}(con código <strong>{promoApplied.code}</strong>, antes ${purchaseData?.amount})
              </>
            )}
            .
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        {renderInstructionsBlock()}

        <TextField
          fullWidth
          size="small"
          label={
            selectedGateway.code === 'binance'
              ? 'Hash / TXID de la transacción'
              : 'Número de referencia / confirmación'
          }
          value={confirmationNumber}
          onChange={(e) => setConfirmationNumber(e.target.value)}
        />

        <Box>
          <Button
            component="label"
            variant={proofFile ? 'outlined' : 'contained'}
            startIcon={<UploadIcon />}
            fullWidth
            color={proofFile ? 'success' : 'primary'}
          >
            {proofFile ? 'Cambiar comprobante' : 'Subir comprobante (imagen/PDF)'}
            <input
              hidden
              accept="image/*,application/pdf"
              type="file"
              onChange={(e) => setProofFile(e.target.files?.[0] || null)}
            />
          </Button>
          {proofFile && (
            <Stack direction="row" spacing={1} alignItems="center" mt={1}>
              <FileIcon fontSize="small" />
              <Typography variant="caption">{proofFile.name}</Typography>
              <Chip size="small" color="success" label="Listo" />
            </Stack>
          )}
        </Box>
      </Stack>
    );
  };

  // -------------------------------------------------------------------------
  // Validación de avance
  // -------------------------------------------------------------------------
  const canProceedFromStep1 = !!selectedCode;
  const canSubmit =
    !!selectedGateway &&
    (selectedCode === 'stripe' ||
      (isManualMethod(selectedCode) && (confirmationNumber.trim() || proofFile)));

  return (
    <Dialog open={openPayment} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Adquirir Plan
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (t) => t.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderSummary()}
        {activeStep === 1 && renderMethodPicker()}
        {activeStep === 2 && renderConfirmation()}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={submitting} color="inherit">
          Cancelar
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={submitting}>
            Atrás
          </Button>
        )}
        {activeStep < STEPS.length - 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={(activeStep === 1 && !canProceedFromStep1) || submitting}
          >
            Continuar
          </Button>
        )}
        {activeStep === STEPS.length - 1 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {selectedCode === 'stripe' ? 'Ir a pagar con Stripe' : 'Confirmar pago'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
